import React, { useState } from "react";
import {
  CustomInputField,
  CustomDatePicker,
  CustomMobileInput,
  CustomTimePicker,
} from "../FormInputs";
import theme from "../../../assets/theme";
import {
  FormLabel,
  IconButton,
  InputAdornment,
  TextareaAutosize,
  makeStyles,
  Button,
} from "@material-ui/core";
import CustomSelect from "../CustomSelect/CustomSelect";
import COLORS from "../../../assets/Colors";
import styled, { css } from "styled-components";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { get } from "lodash";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import NumberFormat from "react-number-format";
import { HyperLink } from "../../internal/PartyDetails";

const useStyles = makeStyles((theme) => ({
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "16px",
    backgroundColor: "#ffffff",
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
  },
}));

const filter = createFilterOptions();

export default function FormField({
  touched,
  errors,
  input,
  values,
  handleChange,
  handleBlur,
  menuItems,
  setFieldValue,
  white = false,
  showAllInitial = false,
  selectedIndex = "label",
  alias,
  multiline,
  row = 4,
  disabled,
  maxLength = 1000,
  style = {},
  minDate,
  maxDate,
  openTo,
  views,
  format = "DD/MM/YYYY",
  placeholderText,
  setFind,
  selectedItemChanged = () => { },
}) {
  const {
    name,
    label,
    type,
    placeholder,
    required,
    autoFocus,
    discription,
    link,
  } = input;
  // A select can display an ID while the form stores a related value (for
  // example, templateCode). This keeps the submitted form data unchanged.
  const fieldValue = input.value !== undefined ? input.value : values[name];
  const [visibility, setVisibility] = useState(false);
  const [searchInput, setSearchInput] = useState();
  const [open, setOpen] = useState(false);
  const classes = useStyles();

  const createNewOption = (options, params) => {
    const filtered = filter(options, params);
    if (params.inputValue !== "") {
      let temp = {};
      temp["inputValue"] = params.inputValue;
      temp[selectedIndex] = `Add "${params.inputValue}"`;
      filtered.push(temp);
    }
    return filtered;
  };

  const calRenderOption = (option) => {
    // Value selected with enter, right from the input
    if (typeof option === "string") {
      return option;
    }
    // Add "xxx" option created dynamically
    if (option.inputValue) {
      return option.inputValue;
    }
    // Regular option
    return option[selectedIndex];
  };

  const autoHandleChange = (event, newValue, name) => {
    if (typeof newValue === "string") {
      setFieldValue(name, newValue?.value);
    } else if (newValue && newValue.inputValue) {
      // Create a new value from the user input
      setFieldValue(name, newValue?.inputValue);
      setFind(true);
    } else if (newValue === null) {
      setFieldValue(name, "");
      setFind(false);
    } else {
      setFieldValue(name, newValue?.value);
    }
  };

  return (
    <div className="form-fields" style={{ width: "100%", paddingTop: 5 }}>
      <FormLabel
        error={errors[name] && touched[name]}
        style={{
          fontFamily: theme.fonts.primaryFontSemiBold,
          fontSize: 12,
          color: COLORS.INPUT_LABEL,
        }}
      >
        {label}
        {required && <span className="required-star"> *</span>}
      </FormLabel>
      {type === "autoselect" ? (
        <Autocomplete
          freeSolo
          disableClearable
          options={showAllInitial || searchInput?.trim() ? menuItems : []}
          name={name}
          value={get(values, name)}
          onChange={(_, v) => {
            v[selectedIndex] = v["inputValue"]
              ? v["inputValue"]
              : v[selectedIndex];
            selectedItemChanged(v);
          }}
          clearOnBlur
          filterOptions={(options, params) => createNewOption(options, params)}
          getOptionLabel={(option) => calRenderOption(option)}
          renderOption={(option) => (
            <React.Fragment>
              {option[selectedIndex]} {option[alias] && `(${option[alias]})`}
            </React.Fragment>
          )}
          onBlur={handleBlur}
          id={name}
          style={{ marginTop: 6 }}
          disabled={disabled}
          renderInput={(params) => (
            <CustomInputField
              {...params}
              error={errors[name] && touched[name]}
              helperText={touched[name] && errors[name]}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              onBlur={handleBlur}
              className={`input-no-padding ${white ? "input-white" : ""}`}
              variant="outlined"
              InputProps={{ ...params.InputProps, type: "search" }}
              placeholder={placeholder}
            />
          )}
        />
      ) : type === "autoComplete" ? (
        <Autocomplete
          classes={{ listbox: classes.listbox }}
          value={values[name]}
          onChange={(event, newValue) =>
            autoHandleChange(event, newValue, name)
          }
          filterOptions={(options, params) => createNewOption(options, params)}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          options={menuItems}
          getOptionLabel={(option) => calRenderOption(option)}
          style={{ marginTop: 6, marginBottom: 10 }}
          renderInput={(params) => (
            <CustomInputField
              {...params}
              error={errors[name] && touched[name]}
              helperText={touched[name] && errors[name]}
              variant="outlined"
            />
          )}
        />
      ) : type === "select" ? (
        <>
          <CustomSelect
            white={white}
            id={name}
            menuItemValues={menuItems}
            name={name}
            value={fieldValue}
            onChange={input.onChange || handleChange}
            helperText={touched[name] && errors[name]}
            error={errors[name] && touched[name]}
            onBlur={handleBlur}
            disabled={disabled}
            style={{ marginTop: 6 }}
          />
          {input.previewUrl && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 5 }}>
              <Button
                type="button"
                disableElevation
                variant="contained"
                onClick={input.onPreview}
                style={{
                  backgroundColor: COLORS.PRIMARY_WHITE,
                  boxShadow: "none",
                  width: 77,
                  height: 30,
                  color: COLORS.BTN_GREEN,
                  textTransform: "capitalize",
                  border: "solid 1px #00838c",
                  fontFamily: theme.fonts.primaryFontBold,
                  fontSize: 14,
                }}
              >
                Preview
              </Button>
            </div>
          )}
        </>
      ) : type === "date" ? (
        <CustomDatePicker
          className={white ? "input-white" : ""}
          inputVariant="outlined"
          openTo={openTo}
          minDate={minDate}
          maxDate={maxDate}
          format={format}
          views={views}
          placeholder={placeholderText}
          value={values[name]}
          onChange={(e) => setFieldValue(name, e)}
          error={errors[name] && touched[name]}
          helperText={touched[name] && errors[name]}
          style={{ marginTop: 6 }}
        />
      ) : type === "time" ? (
        <CustomTimePicker
          className={white ? "input-white" : ""}
          clearable
          inputVariant="outlined"
          ampm={true}
          placeholder={placeholderText}
          error={errors[name] && touched[name]}
          helperText={touched[name] && errors[name]}
          value={values[name]}
          onChange={(e) => setFieldValue(name, e)}
          style={{ marginTop: 6 }}
        />
      ) : name === "mobile" ? (
        <CustomMobileInput
          className={white ? "input-white" : ""}
          defaultCountry={"in"}
          onChange={(e) => setFieldValue(name, e)}
          onBlur={handleBlur}
          autoFormat={false}
          style={{ marginTop: 6 }}
          value={values[name]}
          error={errors[name] && touched[name]}
          helperText={touched[name] && errors[name]}
          id={name}
          placeholder={placeholder}
          name={name}
          variant="outlined"
        />
      ) : type === "numberFormat" ? (
        <NumberFormat
          thousandSeparator
          thousandsGroupStyle="lakh"
          isNumericString={true}
          decimalScale={2}
          fixedDecimalScale
          allowNegative={false}
          className={"input-white"}
          displayType={"input"}
          value={values[name]}
          disabled={disabled}
          type={"text"}
          customInput={CustomInputField}
          name={name}
          onChange={handleChange}
          variant="outlined"
          error={errors[name] && touched[name]}
          helperText={touched[name] && errors[name]}
          onBlur={handleBlur}
        />
      ) : type === "textarea" ? (
        <Relative>
          <CustomTextArea
            className={
              white && multiline
                ? "input-white input-height-auto"
                : white
                  ? "input-white"
                  : multiline
                    ? "input-height-auto"
                    : ""
            }
            disabled={disabled}
            rowsMin={5}
            rowsMax={5}
            autoComplete={"new-password"}
            type={type}
            value={values[name]}
            error={errors[name] && touched[name]}
            helperText={touched[name] && errors[name]}
            id={name}
            maxLength={1000}
            placeholder={placeholder}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            style={{ marginTop: 6, ...style }}
          />
          {maxLength > 0 && (
            <MaxLength>
              {maxLength - values[name]?.length} characters remaining
            </MaxLength>
          )}
          <HelperText>{touched[name] && errors[name]}</HelperText>
        </Relative>
      ) : (
        <Relative>
          <CustomInputField
            className={
              white && multiline
                ? "input-white input-height-auto"
                : white
                  ? "input-white"
                  : multiline
                    ? "input-height-auto"
                    : ""
            }
            multiline={multiline}
            row={4}
            autoComplete={"new-password"}
            autoFocus={name === "fullname" || autoFocus}
            type={type === "password" ? (visibility ? "text" : type) : type}
            value={values[name]}
            error={errors[name] && touched[name]}
            helperText={touched[name] && errors[name]}
            id={name}
            placeholder={placeholder}
            name={name}
            variant="outlined"
            onChange={handleChange}
            onBlur={handleBlur}
            onWheel={(e) => {
              if (type === "number") {
                e.target.blur();
              }
            }}
            onKeyDown={(e) => {
              if (
                type === "number" &&
                (e.key === "ArrowUp" || e.key === "ArrowDown")
              ) {
                e.preventDefault();
              }
            }}
            InputProps={{
              endAdornment: (
                <>
                  {type === "password" ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setVisibility(!visibility)}
                        edge="end"
                      >
                        {visibility ? (
                          <Visibility style={{ fontSize: 18 }} />
                        ) : (
                          <VisibilityOff style={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ) : null}
                </>
              ),
            }}
            style={{ marginTop: 6, marginBottom: 10 }}
          />

          {discription?.length > 0 && (
            <MaxLength
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              {discription}{" "}
              <HyperLink onClick={() => window.open(link)}>emsigner</HyperLink>
            </MaxLength>
          )}
        </Relative>
      )}
    </div>
  );
}

const Relative = styled.div`
  position: relative;
`;

export const CustomTextArea = styled(TextareaAutosize)`
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  font-size: 16px;
  color: ${COLORS.COLOR_DARK} !important;
  font-family: ${theme.fonts.primaryFontSemiBold};
  /* min-height: 89px; */
  border-radius: 6px;
  border: solid 0.5px #e1e3ee;
  background-color: #ffffff;
  outline: none;
  padding: 15px;
  ${({ disabled }) =>
    !disabled
      ? css`
          :hover {
            border: solid 0.5px black;
          }
        `
      : css`
          background-color: ${COLORS.TABLE_GREY_CELL};
        `}

  ${({ error }) =>
    error &&
    css`
      border: solid 0.5px #f44336;
      :hover {
        border: solid 0.5px #f44336;
      }
    `}
`;

const MaxLength = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 10px;
  color: #acb1c2;
  text-align: right;
`;

export const HelperText = styled.div`
  position: absolute;
  left: 3px;
  position: absolute;
  font-size: 10px;
  font-family: openSans-SemiBold, sans-serif;
  color: #f44336;
`;
